import * as Yup from "yup";
import * as uuid from "uuid";
import Step from "@material-ui/core/Step";
import StepContent from "@material-ui/core/StepContent";
import StepLabel from "@material-ui/core/StepLabel";
import Stepper from "@material-ui/core/Stepper";
import React, { FC, useEffect, useRef, useState } from "react";
import { Form, Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";

import AttachAssetsStep from "./steps/AttachAssetsStep";
import Button from "../../../../../components/elements/Button";
import FormErrorHandler from "../../../../../components/elements/FormErrorHandler";
import Loader from "../../../../../components/elements/Loader";
import NameAndDescriptionFillStep from "./steps/NameAndDescriptionFillStep";
import StepIcon from "../../../../../components/modules/BaseStepper/StepIcon";
import api from "../../../../../api";
import classes from "./AdGroupCreationForm.module.scss";
import { AdGroupTemplateCreationBody, AdGroupType } from "../../../../../types/adGroupTypes";
import { AdGroupToCreateType } from "../../../../../types/campaignTypes";
import { AdvertiserType } from "../../../../../types/advertiserTypes";
import { CreativeAssetFromFile } from "../../../../../types/creativeTypes";
import { PageStepComponentProps } from "../../../../../components/modules/BaseStepper/BaseStepper";
import { ReduxApplicationState } from "../../../../../types/ReduxTypes/ReduxApplicationState";
import {
  createAdGroup,
  createAdGroupTemplate,
  createCreativeTemplates,
  setAdGroupTemplate,
  setAdGroupToCreate,
  uploadCreativeAssets,
} from "../../../../../redux/reducers/campaignToCreate";
import { errorToaster } from "../../../../../helpers/toasters";

type FormInitialValues = {
  name: string;
  description: string;
  creativeAssets: CreativeAssetFromFile[];
};

type AdGroupCreationFormProps = {
  updateAdGroup?: (values: AdGroupToCreateType) => void;
};

const createAdGroupAndCreatives = async (
  { name, description, creativeAssets }: FormInitialValues,
  currentUser: string,
  currentAdvertiser: AdvertiserType,
  adGroupType: AdGroupType,
) => {
  try {
    const adGroupToCreateBody = {
      adGroupId: `bg-ad-group-${uuid.v4()}`,
      brandgenAdvertiserId: currentAdvertiser?.brandgenAdvertiserId!,
      brandgenCampaignId: "",
      createdBy: currentUser,
      name,
      adGroupType,
      description: description ?? "",
    };
    const createdAdGroup = await createAdGroup(adGroupToCreateBody);
    if (createdAdGroup) {
      const adGroupTemplateToCreateBody: AdGroupTemplateCreationBody = {
        id: `bg-ad-group-template-${uuid.v4()}`,
        brandgenAdvertiserId: createdAdGroup.brandgenAdvertiserId!,
        createdBy: currentUser,
        name,
        adGroupType,
        description,
      };

      const createdAdGroupTemplate = await createAdGroupTemplate(adGroupTemplateToCreateBody);

      try {
        const uploadedCreatives = await uploadCreativeAssets(
          creativeAssets,
          currentAdvertiser!.beeswaxAdvertiserId.toString(),
          currentAdvertiser!.brandgenAdvertiserId,
          "https://www.domain.com",
          createdAdGroup.adGroupId,
          api.createCreative,
        );

        await createCreativeTemplates(uploadedCreatives, createdAdGroupTemplate.id);
      } catch (error) {
        console.error("Error uploading creatives or creating templates", error);
        throw error;
      }
    }
  } catch (error) {
    const err = error as any;
    throw new Error(err.message);
  }
};

const AdGroupCreationForm: FC<PageStepComponentProps & AdGroupCreationFormProps> = ({
  isAttemptingToGoNext,
  setIsAttemptingToGoNext,
  handleNext,
  updateAdGroup,
}) => {
  const adGroupToCreate = useSelector((state: ReduxApplicationState) => state.campaignToCreate.adGroupToCreate);

  const currentUser = useSelector((state: ReduxApplicationState) => state.user.currentUser);
  const currentAdvertiser = useSelector((state: ReduxApplicationState) => state.campaignToCreate.selectedAdvertiser);

  const formInitialValues: FormInitialValues = {
    name: adGroupToCreate.name ?? "",
    description: adGroupToCreate.description ?? "",
    creativeAssets:
      adGroupToCreate.creativeAssets.length !== 0
        ? adGroupToCreate.creativeAssets.filter((asset): asset is CreativeAssetFromFile => "file" in asset)
        : [],
  };

  const validationSchema = Yup.object({
    name: Yup.string().required("Required field."),
  });

  const [activeStep, setActiveStep] = useState(() => {
    return adGroupToCreate.creativeAssets.length !== 0 ? 1 : 0;
  });

  const [isLoading, setIsLoading] = useState(false);

  const initialRef: any = null;
  const hiddenInnerSubmitFormRef = useRef(initialRef);

  const dispatch = useDispatch();

  const handleNextStep = () => {
    if (setIsAttemptingToGoNext) {
      setIsAttemptingToGoNext(false);
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handlePrevStep = () => {
    if (setIsAttemptingToGoNext) {
      setIsAttemptingToGoNext(false);
    }
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async (values: FormInitialValues) => {
    const { name, description, creativeAssets } = values;

    if (activeStep) {
      if (setIsAttemptingToGoNext && handleNext) {
        if (creativeAssets.length !== 0) {
          const adGroupType = creativeAssets[0].fileType as AdGroupType;
          dispatch(
            setAdGroupToCreate({
              name,
              description,
              adGroupType,
              creativeAssets,
            }),
          );
          setIsAttemptingToGoNext(false);
          handleNext();
        } else {
          errorToaster("Please, upload creative assets to continue.", 5000);
          setIsAttemptingToGoNext(false);
        }
      } else {
        try {
          setIsLoading(true);
          const adGroupType = creativeAssets[0].fileType as AdGroupType;

          if (updateAdGroup) {
            updateAdGroup({
              name,
              description,
              adGroupType,
              creativeAssets,
            });
            setIsLoading(false);
            setActiveStep((prevActiveStep) => prevActiveStep - 1);
            return;
          }
          await createAdGroupAndCreatives(
            { name, description, creativeAssets },
            currentUser,
            currentAdvertiser!,
            adGroupType,
          );
          const { payload: adGroupsTemplates } = await api.getAdGroupTemplatesByAdvertiser(
            currentAdvertiser?.brandgenAdvertiserId!,
          );
          dispatch(setAdGroupTemplate(adGroupsTemplates));
          setIsLoading(false);
          setActiveStep((prevActiveStep) => prevActiveStep - 1);
        } catch (error) {
          setIsLoading(false);
          errorToaster(error);
        }
      }
    } else {
      handleNextStep();
    }
  };

  useEffect(() => {
    if (isAttemptingToGoNext) {
      hiddenInnerSubmitFormRef!.current!.click();
    }
  }, [isAttemptingToGoNext]);

  const handleInvalidSubmit = () => {
    if (setIsAttemptingToGoNext) setIsAttemptingToGoNext(false);
  };

  const handleCreateAdGroupAndCreatives = async () => {
    hiddenInnerSubmitFormRef!.current!.click();
  };

  return (
    <div className={classes.container}>
      {isLoading ? (
        <Loader size={60} top="50%" />
      ) : (
        <Formik
          initialValues={formInitialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnBlur={false}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <FormErrorHandler action={handleInvalidSubmit} />
              <div className={classes.formControls}>
                <button ref={hiddenInnerSubmitFormRef} className={classes.hiddenBtn} type="submit">
                  Submit
                </button>

                <Button disabled={activeStep === 0} onClick={handlePrevStep} variant="outlined">
                  Back
                </Button>

                <Button disabled={activeStep === 1} type="submit">
                  Next
                </Button>
              </div>
              <Stepper
                activeStep={activeStep}
                orientation="vertical"
                className={`${classes.stepper} ${!setIsAttemptingToGoNext && classes.hideBackground}`}
              >
                <Step>
                  <StepLabel StepIconComponent={StepIcon}>Name and Description</StepLabel>
                  <StepContent>
                    <NameAndDescriptionFillStep values={values} />
                  </StepContent>
                </Step>
                <Step>
                  <StepLabel StepIconComponent={StepIcon}>
                    Choose Files to upload for this campaign's ad group
                  </StepLabel>

                  <StepContent>
                    <AttachAssetsStep
                      creativeAssets={values.creativeAssets}
                      setFieldValue={setFieldValue}
                      limit={100}
                      isFromPage={!setIsAttemptingToGoNext}
                      handleBtnSave={handleCreateAdGroupAndCreatives}
                    />
                  </StepContent>
                </Step>
              </Stepper>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
};

export default AdGroupCreationForm;
